/* ==========================================================================
   SCROLL ANIMATIONS - Maison by Ola
   Intersection Observer based reveal animations
   ========================================================================== */

class ScrollReveal {
  constructor(options = {}) {
    this.defaults = {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px',
      selector: '[data-reveal]'
    };

    this.options = { ...this.defaults, ...options };
    this.elements = document.querySelectorAll(this.options.selector);

    // Respect reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) {
      this.showAllElements();
      return;
    }

    this.init();
  }

  init() {
    if (!this.elements.length) return;

    // No IntersectionObserver (very old browser) → just show everything.
    if (!('IntersectionObserver' in window)) {
      this.showAllElements();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      {
        // threshold 0 + no negative margin = fire the instant any pixel enters,
        // which shrinks the window where a fast flick-scroll can be missed.
        threshold: 0,
        rootMargin: '0px'
      }
    );

    this.elements.forEach(el => {
      this.observer.observe(el);
    });

    // Safety net for fast scrolling on mobile: IntersectionObserver coalesces
    // rapid changes and can deliver only a final non-intersecting state for an
    // element that flew past between frames — leaving it stuck at opacity:0.
    // Sweep once after load to reveal anything already in or above the viewport.
    this.sweep = this.sweep.bind(this);
    window.addEventListener('load', this.sweep, { once: true });
  }

  handleIntersect(entries) {
    entries.forEach(entry => {
      // Reveal if currently intersecting OR already scrolled past. On a fast
      // flick the entry delivered may be non-intersecting because the element
      // is now ABOVE the viewport — detect that via boundingClientRect so it
      // never gets left hidden. CSS handles the stagger via transition-delay.
      const viewportBottom = entry.rootBounds
        ? entry.rootBounds.bottom
        : window.innerHeight;

      if (entry.isIntersecting || entry.boundingClientRect.top < viewportBottom) {
        this.reveal(entry.target);
      }
    });
  }

  reveal(el) {
    el.classList.add('is-revealed');
    if (this.observer) this.observer.unobserve(el);
  }

  sweep() {
    const viewportBottom = window.innerHeight;
    this.elements.forEach(el => {
      if (el.classList.contains('is-revealed')) return;
      if (el.getBoundingClientRect().top < viewportBottom) this.reveal(el);
    });
  }

  showAllElements() {
    this.elements.forEach(el => {
      el.classList.add('is-revealed');
    });
  }

  // Allow adding new elements dynamically
  observe(element) {
    if (this.prefersReducedMotion) {
      element.classList.add('is-revealed');
      return;
    }

    if (this.observer) {
      this.observer.observe(element);
    }
  }
}

/* ==========================================================================
   Parallax Effect
   ========================================================================== */

class Parallax {
  constructor(selector = '[data-parallax]') {
    this.elements = document.querySelectorAll(selector);

    // Respect reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion || !this.elements.length) return;

    this.init();
  }

  init() {
    // Use requestAnimationFrame for smooth animation
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', () => {
      requestAnimationFrame(this.handleScroll);
    });

    // Initial call
    this.handleScroll();
  }

  handleScroll() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;

    this.elements.forEach(el => {
      const rect = el.getBoundingClientRect();
      const elementTop = rect.top + scrollY;
      const speed = parseFloat(el.dataset.parallaxSpeed) || 0.3;

      // Only apply parallax when element is in or near viewport
      if (rect.bottom > 0 && rect.top < viewportHeight) {
        const yOffset = (scrollY - elementTop + viewportHeight) * speed;
        el.style.transform = `translateY(${yOffset}px)`;
      }
    });
  }
}

/* ==========================================================================
   Stagger Animation
   For animating lists/grids with staggered delays
   ========================================================================== */

class StaggerAnimation {
  constructor(container, options = {}) {
    this.container = container;
    if (!this.container) return;

    this.defaults = {
      childSelector: '[data-stagger-item]',
      baseDelay: 0,
      delayIncrement: 100,
      threshold: 0.1
    };

    this.options = { ...this.defaults, ...options };
    this.children = this.container.querySelectorAll(this.options.childSelector);

    // Respect reduced motion
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) {
      this.showAllChildren();
      return;
    }

    this.init();
  }

  init() {
    if (!this.children.length) return;

    // Set initial state and stagger delays
    this.children.forEach((child, index) => {
      child.style.opacity = '0';
      child.style.transform = 'translateY(20px)';
      child.style.transitionDelay = `${this.options.baseDelay + (index * this.options.delayIncrement)}ms`;
    });

    // Observe container
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animate();
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: this.options.threshold }
    );

    this.observer.observe(this.container);
  }

  animate() {
    this.children.forEach(child => {
      child.style.opacity = '1';
      child.style.transform = 'translateY(0)';
    });
  }

  showAllChildren() {
    this.children.forEach(child => {
      child.style.opacity = '1';
      child.style.transform = 'translateY(0)';
    });
  }
}

/* ==========================================================================
   Initialize
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize scroll reveal
  const scrollReveal = new ScrollReveal();

  // Initialize parallax (if any parallax elements exist)
  new Parallax();

  // Initialize stagger animations
  document.querySelectorAll('[data-stagger-container]').forEach(container => {
    new StaggerAnimation(container);
  });

  // Export for dynamic use
  window.MaisonByOla = window.MaisonByOla || {};
  window.MaisonByOla.ScrollReveal = scrollReveal;
  window.MaisonByOla.StaggerAnimation = StaggerAnimation;
});
